import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useProperty } from "../../context/PropertyContext";
import { usePageAssistantContext } from "../../context/PageAssistantContext";
import {
  assistantChat,
  assistantFillPage,
  assistantLeaseExtractFields,
} from "../../utils/assistant/api";
import { extractLeaseTextFromFile } from "../../leases/import/leaseImportUtils";
import {
  buildLeaseLlmUnresolvedPayload,
  buildLeaseRequiredChecklist,
  computeFileSha256,
  mergeLeaseFieldProposals,
  runDeterministicLeaseExtraction,
  selectLeasePageImagesForLlm,
  validateLeaseLlmResultShape,
} from "../../utils/assistant/leaseExtractionPipeline";
import { mapLeaseFieldsToPageProposals } from "../../utils/assistant/fieldMapping";

function safeString(value) {
  return String(value == null ? "" : value).trim();
}

function confidenceClass(confidence) {
  const n = Number(confidence || 0);
  if (n >= 0.85) return "text-green-700";
  if (n >= 0.6) return "text-yellow-700";
  return "text-red-700";
}

function normalizeFieldText(value) {
  return String(value == null ? "" : value);
}

function actionButtonClass(active) {
  return active
    ? "px-2 py-1 rounded text-xs font-semibold bg-indigo-700 text-white"
    : "px-2 py-1 rounded text-xs font-semibold border border-gray-300 hover:bg-gray-50";
}

export default function PropertyAssistantDock({ enabled }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeProperty } = useProperty();
  const { getPageContext, applyFieldValue, applyFieldPatch } = usePageAssistantContext();

  const [open, setOpen] = useState(false);
  const [action, setAction] = useState("ask");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [askInput, setAskInput] = useState("");
  const [messages, setMessages] = useState([]);

  const [fillProposals, setFillProposals] = useState([]);
  const [fillSnapshot, setFillSnapshot] = useState(null);

  const [importFile, setImportFile] = useState(null);
  const [importProgress, setImportProgress] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [importFields, setImportFields] = useState({});

  const hiddenRoutes = useMemo(() => new Set([
    "/",
    "/manager-login",
    "/tenant-login",
  ]), []);

  useEffect(() => {
    setError("");
    setFillProposals([]);
    setFillSnapshot(null);
  }, [location.pathname]);

  if (!enabled) return null;
  if (hiddenRoutes.has(location.pathname)) return null;

  const propertyId = activeProperty && activeProperty !== "all" ? activeProperty : "";

  async function runAsk(intentMode) {
    const prompt = safeString(askInput) || (intentMode === "explain" ? "Explain this page and what I can do here." : "Help me with this page.");
    if (!propertyId) {
      setError("Select a property first.");
      return;
    }

    const pageContext = getPageContext();
    setBusy(true);
    setError("");
    setMessages((prev) => [...prev, { role: "user", text: prompt }]);

    try {
      const data = await assistantChat({
        assistant_intent: "qa",
        propertyId,
        mode: "APP_HELP",
        message: prompt,
        pageContext,
      });
      const reply = safeString(data?.reply) || "No response generated.";
      setMessages((prev) => [...prev, { role: "assistant", text: reply }]);
      setAskInput("");
    } catch (err) {
      setError(String(err?.diagnostic?.message || err?.message || "Assistant request failed."));
    } finally {
      setBusy(false);
    }
  }

  async function runFillPage() {
    if (!propertyId) {
      setError("Select a property first.");
      return;
    }

    const pageContext = getPageContext();
    if (!pageContext.fillableFields.length) {
      setError("No fillable fields were detected on this page.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const data = await assistantFillPage({
        propertyId,
        mode: "APP_HELP",
        message: safeString(askInput) || "Propose field values for this page.",
        pageContext,
      });
      const proposals = Array.isArray(data?.proposals) ? data.proposals : [];
      setFillSnapshot(pageContext);
      setFillProposals(proposals);
      if (!proposals.length) {
        setError("No field proposals were returned.");
      }
    } catch (err) {
      setError(String(err?.diagnostic?.message || err?.message || "Fill page request failed."));
    } finally {
      setBusy(false);
    }
  }

  function applyOneProposal(proposal) {
    if (!proposal) return;
    const ok = applyFieldValue(proposal.fieldKey, proposal.value);
    if (!ok) {
      setError(`Could not apply value to ${proposal.fieldKey}.`);
      return;
    }
    setFillProposals((prev) => prev.filter((row) => row.fieldKey !== proposal.fieldKey));
  }

  function applyAllProposals() {
    if (!fillProposals.length) return;
    const applied = applyFieldPatch(fillProposals);
    if (applied <= 0) {
      setError("No proposals could be applied on this page.");
      return;
    }
    setFillProposals([]);
  }

  async function runLeaseScan() {
    if (!propertyId) {
      setError("Select a property first.");
      return;
    }
    if (!importFile) {
      setError("Select a lease file first.");
      return;
    }

    setBusy(true);
    setError("");
    setImportResult(null);
    setImportFields({});

    try {
      setImportProgress("Extracting text...");
      const extracted = await extractLeaseTextFromFile(importFile, {
        maxPdfPages: 20,
        forceOcrForPdf: true,
        capturePageImages: true,
        maxImagePages: 8,
        imageMaxWidth: 1280,
        imageQuality: 0.7,
        onProgress: (progress) => {
          const page = progress?.pageNumber && progress?.totalPages
            ? ` (page ${progress.pageNumber}/${progress.totalPages})`
            : "";
          const label = safeString(progress?.label || progress?.stage || "processing");
          setImportProgress(`${label}${page}`);
        },
      });

      setImportProgress("Running deterministic extraction...");
      const deterministic = runDeterministicLeaseExtraction({
        rawText: extracted.text,
        formFields: extracted.formFields || {},
      });

      const fileHash = await computeFileSha256(importFile);
      const unresolvedFields = buildLeaseLlmUnresolvedPayload(deterministic, 0.92, {
        includeFieldKeys: [
          "tenantFullName",
          "tenantEmail",
          "suiteNumber",
          "rentAmount",
          "startDate",
          "endDate",
          "securityDeposit",
        ],
      });
      let llmFields = {};
      let llmCalled = false;
      let cacheHit = false;

      if (unresolvedFields.length) {
        setImportProgress("Resolving low-confidence fields with AI + page visuals...");
        const assistantDocumentContext = String(extracted.text || "").slice(0, 16000);
        const assistantPageImages = selectLeasePageImagesForLlm(
          extracted.pageImages,
          unresolvedFields,
          { maxPages: 4 }
        );
        const response = await assistantLeaseExtractFields({
          propertyId,
          fileHash,
          extractionVersion: deterministic.extractionVersion,
          documentContext: assistantDocumentContext,
          pageImages: assistantPageImages,
          unresolvedFields,
        });

        llmCalled = Boolean(response?.llmCalled);
        cacheHit = Boolean(response?.cacheHit);
        llmFields = validateLeaseLlmResultShape(
          response?.fields,
          unresolvedFields.map((row) => row.fieldKey)
        );
      }

      const mergedFields = mergeLeaseFieldProposals({
        heuristicFields: deterministic.fields,
        llmFields,
      });
      const requiredChecklist = buildLeaseRequiredChecklist(mergedFields);

      setImportFields(mergedFields);
      setImportResult({
        extractionVersion: deterministic.extractionVersion,
        fileHash,
        requiredChecklist,
        llmCalled,
        cacheHit,
      });
      setImportProgress("Lease scan complete. Review and apply suggestions.");
    } catch (err) {
      setImportProgress("");
      setError(String(err?.diagnostic?.message || err?.message || "Lease scan failed."));
    } finally {
      setBusy(false);
    }
  }

  function updateImportField(fieldKey, value) {
    setImportFields((prev) => ({
      ...(prev || {}),
      [fieldKey]: {
        ...(prev?.[fieldKey] || {}),
        value: normalizeFieldText(value),
      },
    }));
  }

  function applyLeaseToCurrentPage() {
    const pageContext = getPageContext();
    const proposals = mapLeaseFieldsToPageProposals(importFields, pageContext);
    if (!proposals.length) {
      setError("No matching form fields were found on this page for lease import data.");
      return;
    }

    const applied = applyFieldPatch(proposals);
    if (applied <= 0) {
      setError("No lease fields could be applied to this page.");
      return;
    }

    setError("");
  }

  function openLeaseImportPage() {
    navigate("/leases/import");
    setOpen(false);
  }

  return (
    <div className="fixed bottom-3 right-3 z-50 w-[calc(100%-1.5rem)] sm:w-[380px]">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full sm:w-auto ml-auto flex items-center gap-2 px-4 py-2 rounded-full bg-gray-900 text-white shadow-lg hover:bg-black"
        >
          <span className="text-sm font-semibold">AI Assistant</span>
        </button>
      ) : (
        <div className="bg-white border border-gray-300 rounded-xl shadow-2xl overflow-hidden">
          <div className="px-3 py-2 bg-gray-900 text-white flex items-center justify-between">
            <div className="text-sm font-semibold">Property Assistant</div>
            <button type="button" onClick={() => setOpen(false)} className="text-xs px-2 py-1 rounded bg-white/20 hover:bg-white/30">
              Close
            </button>
          </div>

          <div className="p-2 border-b bg-gray-50 flex flex-wrap gap-2">
            <button type="button" className={actionButtonClass(action === "ask")} onClick={() => setAction("ask")}>Ask</button>
            <button type="button" className={actionButtonClass(action === "fill")} onClick={() => setAction("fill")}>Fill This Page</button>
            <button type="button" className={actionButtonClass(action === "explain")} onClick={() => setAction("explain")}>Explain Page</button>
            <button type="button" className={actionButtonClass(action === "import")} onClick={() => setAction("import")}>Import Lease</button>
          </div>

          <div className="p-3 space-y-3 max-h-[70vh] overflow-y-auto">
            {!propertyId && (
              <div className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded p-2">
                Select a property to enable assistant actions.
              </div>
            )}

            {error ? (
              <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2">{error}</div>
            ) : null}

            {(action === "ask" || action === "explain") && (
              <>
                <div className="space-y-2 max-h-52 overflow-y-auto border rounded p-2 bg-gray-50">
                  {!messages.length ? (
                    <div className="text-xs text-gray-600">Ask a question about this page and property context.</div>
                  ) : (
                    messages.map((row, idx) => (
                      <div
                        key={`${row.role}-${idx}`}
                        className={row.role === "user"
                          ? "text-xs bg-indigo-700 text-white rounded p-2 ml-8"
                          : "text-xs bg-white border rounded p-2 mr-8"
                        }
                      >
                        {row.text}
                      </div>
                    ))
                  )}
                </div>
                <textarea
                  value={askInput}
                  onChange={(event) => setAskInput(event.target.value)}
                  rows={3}
                  className="w-full border rounded px-2 py-2 text-sm"
                  placeholder={action === "explain" ? "Optional: ask what to focus on" : "Ask about this page"}
                />
                <button
                  type="button"
                  onClick={() => runAsk(action === "explain" ? "explain" : "ask")}
                  disabled={busy || !propertyId}
                  className="w-full px-3 py-2 rounded bg-indigo-700 text-white text-sm font-semibold disabled:opacity-60"
                >
                  {busy ? "Working..." : action === "explain" ? "Explain This Page" : "Ask Question"}
                </button>
              </>
            )}

            {action === "fill" && (
              <>
                <div className="text-xs text-gray-600">
                  Assistant proposes values for visible fields. Nothing is saved until you save on the page.
                </div>
                <button
                  type="button"
                  onClick={runFillPage}
                  disabled={busy || !propertyId}
                  className="w-full px-3 py-2 rounded bg-indigo-700 text-white text-sm font-semibold disabled:opacity-60"
                >
                  {busy ? "Generating..." : "Generate Field Proposals"}
                </button>

                {fillProposals.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-semibold">Proposed Changes</div>
                      <button type="button" onClick={applyAllProposals} className="text-xs px-2 py-1 rounded border hover:bg-gray-50">
                        Apply All
                      </button>
                    </div>
                    {fillProposals.map((proposal) => {
                      const current = fillSnapshot?.fillableFields?.find((row) => row.fieldKey === proposal.fieldKey);
                      return (
                        <div key={proposal.fieldKey} className="border rounded p-2 bg-gray-50">
                          <div className="text-xs font-semibold">{current?.label || proposal.fieldKey}</div>
                          <div className="text-[11px] text-gray-600 mt-1">Current: {current?.currentValue || "(empty)"}</div>
                          <div className="text-[11px] text-gray-800 mt-1">Proposed: {proposal.value}</div>
                          <div className="flex items-center justify-between mt-1">
                            <div className={`text-[11px] ${confidenceClass(proposal.confidence)}`}>
                              Confidence: {Number(proposal.confidence || 0).toFixed(2)}
                            </div>
                            <button
                              type="button"
                              onClick={() => applyOneProposal(proposal)}
                              className="text-xs px-2 py-1 rounded bg-gray-900 text-white"
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {action === "import" && (
              <>
                <div className="text-xs text-gray-600">
                  Deterministic extraction runs first. AI is only used for unresolved fields.
                </div>
                <input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(event) => setImportFile(event.target.files?.[0] || null)}
                  className="w-full border rounded px-2 py-2 text-xs"
                />
                <button
                  type="button"
                  onClick={runLeaseScan}
                  disabled={busy || !importFile || !propertyId}
                  className="w-full px-3 py-2 rounded bg-indigo-700 text-white text-sm font-semibold disabled:opacity-60"
                >
                  {busy ? "Scanning..." : "Import Lease (Scan)"}
                </button>

                {importProgress ? (
                  <div className="text-xs text-gray-700">Status: {importProgress}</div>
                ) : null}

                {importResult && (
                  <div className="space-y-2">
                    <div className="border rounded p-2 bg-gray-50 text-xs">
                      <div>Extraction version: <span className="font-mono">{importResult.extractionVersion}</span></div>
                      <div>LLM called: {importResult.llmCalled ? "yes" : "no"}</div>
                      <div>Cache hit: {importResult.cacheHit ? "yes" : "no"}</div>
                    </div>

                    <div className="border rounded p-2 bg-gray-50">
                      <div className="text-xs font-semibold">Required Fields</div>
                      <div className="space-y-1 mt-1">
                        {importResult.requiredChecklist.items.map((item) => (
                          <div key={item.fieldKey} className="text-[11px] flex items-center justify-between">
                            <span>{item.label}</span>
                            <span className={item.ready ? "text-green-700" : "text-red-700"}>
                              {item.ready ? "ready" : "review"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {Object.entries(importFields).map(([fieldKey, row]) => (
                        <div key={fieldKey} className="border rounded p-2 bg-white">
                          <div className="text-xs font-semibold">{fieldKey}</div>
                          <input
                            value={row?.value || ""}
                            onChange={(event) => updateImportField(fieldKey, event.target.value)}
                            className="mt-1 w-full border rounded px-2 py-1 text-xs"
                          />
                          <div className={`text-[11px] mt-1 ${confidenceClass(row?.confidence)}`}>
                            Confidence: {Number(row?.confidence || 0).toFixed(2)} ({row?.source || "heuristic"})
                          </div>
                          <div className="text-[11px] text-gray-600">{row?.rationale || ""}</div>
                          {Array.isArray(row?.evidence) && row.evidence.length > 0 && (
                            <details className="mt-1 text-[11px]">
                              <summary className="cursor-pointer">Evidence</summary>
                              <div className="mt-1 space-y-1">
                                {row.evidence.map((ev, index) => (
                                  <div key={`${fieldKey}-${index}`} className="border rounded p-1 bg-gray-50">
                                    <div>Page {Number(ev.page || 0)}</div>
                                    <div className="text-gray-700">{ev.quote}</div>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={applyLeaseToCurrentPage}
                      className="w-full px-3 py-2 rounded border border-gray-300 hover:bg-gray-50 text-sm font-semibold"
                    >
                      Apply Lease Fields To Current Page
                    </button>
                    <button
                      type="button"
                      onClick={openLeaseImportPage}
                      className="w-full px-3 py-2 rounded bg-gray-900 text-white text-sm font-semibold"
                    >
                      Open Lease Import Page
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
